import { useState, useRef } from "react"
import { ArrowLeft, Music2, Mic2, Play, Pause, Loader2, Download, Disc3 } from "lucide-react"

import { Button, Card, CardContent, Slider, Progress } from "@aix/ui"
import { trpc } from "../../../lib/trpc"

import { TrackSelector } from "./TrackSelector"
import { renderMashup, downloadBlob } from "../utils/audioRenderer"

interface Song {
  id: string
  title: string
  artist: string | null
  genre: string
  bpm: number | null
  camelotKey: string | null
  vocalsFileUrl: string | null
  instrumentalFileUrl: string | null
}

interface MashupBuilderProps {
  onBack: () => void
  onCreateProject: (projectId: string) => void
}

export function MashupBuilder({ onBack, onCreateProject }: MashupBuilderProps) {
  const [vocalsSong, setVocalsSong] = useState<Song | null>(null)
  const [instrumentalSong, setInstrumentalSong] = useState<Song | null>(null)
  const [vocalsVolume, setVocalsVolume] = useState(80)
  const [instrumentalVolume, setInstrumentalVolume] = useState(100)
  const [projectName, setProjectName] = useState("")
  const [isPlaying, setIsPlaying] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderedBlob, setRenderedBlob] = useState<Blob | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)

  // Audio refs for preview
  const vocalsAudioRef = useRef<HTMLAudioElement | null>(null)
  const instrumentalAudioRef = useRef<HTMLAudioElement | null>(null)

  const createProjectMutation = trpc.mashupMix.create.useMutation({
    onSuccess: (project) => {
      onCreateProject(project.id)
    },
  })

  const calculateCompatibility = () => {
    if (!vocalsSong?.bpm || !instrumentalSong?.bpm) return null

    const bpmDiff = Math.abs(vocalsSong.bpm - instrumentalSong.bpm)
    let score = 100

    // BPM penalty
    if (bpmDiff <= 2) score -= 0
    else if (bpmDiff <= 5) score -= 10
    else if (bpmDiff <= 10) score -= 30
    else score -= 50

    // Key compatibility bonus
    if (vocalsSong.camelotKey && instrumentalSong.camelotKey) {
      if (vocalsSong.camelotKey === instrumentalSong.camelotKey) {
        score += 10 // Perfect match
      }
    }

    return Math.max(0, Math.min(100, score))
  }

  const compatibility = calculateCompatibility()

  const getCompatibilityColor = (score: number | null) => {
    if (score === null) return "text-muted-foreground"
    if (score >= 80) return "text-green-500"
    if (score >= 60) return "text-yellow-500"
    return "text-red-500"
  }

  const getCompatibilityLabel = (score: number | null) => {
    if (score === null) return "Select songs to see compatibility"
    if (score >= 80) return "Excellent match!"
    if (score >= 60) return "Good match"
    if (score >= 40) return "May need tempo adjustment"
    return "Significant BPM difference"
  }

  const handleCreateProject = () => {
    if (!vocalsSong || !instrumentalSong || !projectName.trim()) return

    createProjectMutation.mutate({
      name: projectName.trim(),
      vocalsSongId: vocalsSong.id,
      instrumentalSongId: instrumentalSong.id,
    })
  }

  const generateProjectName = () => {
    if (vocalsSong && instrumentalSong) {
      const vocalsName = vocalsSong.title.split(" ").slice(0, 2).join(" ")
      const instName = instrumentalSong.title.split(" ").slice(0, 2).join(" ")
      return `${vocalsName} x ${instName}`
    }
    return ""
  }

  const handleRender = async () => {
    if (!vocalsSong?.vocalsFileUrl || !instrumentalSong?.instrumentalFileUrl) return

    setIsRendering(true)
    setRenderProgress(0)
    setRenderError(null)
    setRenderedBlob(null)

    try {
      const result = await renderMashup({
        vocalsUrl: vocalsSong.vocalsFileUrl,
        instrumentalUrl: instrumentalSong.instrumentalFileUrl,
        vocalsVolume: vocalsVolume / 100,
        instrumentalVolume: instrumentalVolume / 100,
        onProgress: setRenderProgress,
      })

      setRenderedBlob(result.blob)
    } catch (error) {
      console.error("Render failed:", error)
      setRenderError(error instanceof Error ? error.message : "Render failed")
    } finally {
      setIsRendering(false)
    }
  }

  const handleDownload = () => {
    if (!renderedBlob) return
    const filename = projectName.trim() || generateProjectName() || "mashup"
    downloadBlob(renderedBlob, `${filename}.wav`)
  }

  const handlePreviewToggle = () => {
    if (!vocalsSong?.vocalsFileUrl || !instrumentalSong?.instrumentalFileUrl) return

    if (isPlaying) {
      vocalsAudioRef.current?.pause()
      instrumentalAudioRef.current?.pause()
      setIsPlaying(false)
    } else {
      // Create audio elements if needed
      if (!vocalsAudioRef.current) {
        vocalsAudioRef.current = new Audio(vocalsSong.vocalsFileUrl)
      }
      if (!instrumentalAudioRef.current) {
        instrumentalAudioRef.current = new Audio(instrumentalSong.instrumentalFileUrl)
      }

      // Set volumes
      vocalsAudioRef.current.volume = vocalsVolume / 100
      instrumentalAudioRef.current.volume = instrumentalVolume / 100

      // Sync and play
      vocalsAudioRef.current.currentTime = 0
      instrumentalAudioRef.current.currentTime = 0
      void vocalsAudioRef.current.play()
      void instrumentalAudioRef.current.play()
      setIsPlaying(true)

      // Stop on end
      vocalsAudioRef.current.onended = () => setIsPlaying(false)
    }
  }

  // Update volumes when sliders change
  if (vocalsAudioRef.current) {
    vocalsAudioRef.current.volume = vocalsVolume / 100
  }
  if (instrumentalAudioRef.current) {
    instrumentalAudioRef.current.volume = instrumentalVolume / 100
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-xl font-bold">Create Mashup</h2>
          <p className="text-sm text-muted-foreground">Combine vocals from one song with instrumental from another</p>
        </div>
      </div>

      {/* Track Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Vocals Track */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-pink-500/10 rounded-lg">
                <Mic2 className="h-5 w-5 text-pink-500" />
              </div>
              <div>
                <h3 className="font-medium">Vocals Track</h3>
                <p className="text-xs text-muted-foreground">Select song for acapella</p>
              </div>
            </div>
            <TrackSelector
              selectedSong={vocalsSong}
              onSelect={setVocalsSong}
              trackType="vocals"
              excludeSongId={instrumentalSong?.id}
            />
            {vocalsSong && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Volume</span>
                  <span className="text-sm font-medium">{vocalsVolume}%</span>
                </div>
                <Slider value={[vocalsVolume]} onValueChange={([v]) => setVocalsVolume(v)} max={100} step={1} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instrumental Track */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Music2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h3 className="font-medium">Instrumental Track</h3>
                <p className="text-xs text-muted-foreground">Select song for beat</p>
              </div>
            </div>
            <TrackSelector
              selectedSong={instrumentalSong}
              onSelect={setInstrumentalSong}
              trackType="instrumental"
              excludeSongId={vocalsSong?.id}
              sourceBpm={vocalsSong?.bpm}
            />
            {instrumentalSong && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Volume</span>
                  <span className="text-sm font-medium">{instrumentalVolume}%</span>
                </div>
                <Slider
                  value={[instrumentalVolume]}
                  onValueChange={([v]) => setInstrumentalVolume(v)}
                  max={100}
                  step={1}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compatibility Score */}
      {(vocalsSong || instrumentalSong) && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Compatibility</h3>
                <p className={`text-sm ${getCompatibilityColor(compatibility)}`}>
                  {getCompatibilityLabel(compatibility)}
                </p>
              </div>
              {compatibility !== null && (
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getCompatibilityColor(compatibility)}`}>{compatibility}%</div>
                  <div className="text-xs text-muted-foreground">
                    BPM: {vocalsSong?.bpm?.toFixed(1) || "--"} vs {instrumentalSong?.bpm?.toFixed(1) || "--"}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview & Create */}
      {vocalsSong && instrumentalSong && (
        <Card>
          <CardContent className="p-4 space-y-4">
            {/* Project Name */}
            <div>
              <label className="text-sm font-medium mb-2 block">Project Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter mashup name..."
                  className="flex-1 px-3 py-2 border border-border rounded-md bg-background"
                />
                <Button variant="outline" size="sm" onClick={() => setProjectName(generateProjectName())}>
                  Auto
                </Button>
              </div>
            </div>

            {/* Preview Controls */}
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                onClick={handlePreviewToggle}
                disabled={!vocalsSong.vocalsFileUrl || !instrumentalSong.instrumentalFileUrl}
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-5 w-5 mr-2" />
                    Pause Preview
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Preview Mix
                  </>
                )}
              </Button>
              <Button
                size="lg"
                className="flex-1"
                onClick={handleCreateProject}
                disabled={!projectName.trim() || createProjectMutation.isLoading}
              >
                {createProjectMutation.isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Mashup Project"
                )}
              </Button>
            </div>

            {/* Render & Download */}
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-sm">Export Mashup</h4>
                  <p className="text-xs text-muted-foreground">Render the mix to a downloadable audio file</p>
                </div>
                {renderedBlob && !isRendering && <span className="text-xs text-green-500">Ready to download</span>}
              </div>

              {isRendering && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Disc3 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm">Rendering... {renderProgress}%</span>
                  </div>
                  <Progress value={renderProgress} className="h-2" />
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleRender}
                  disabled={isRendering || !vocalsSong.vocalsFileUrl || !instrumentalSong.instrumentalFileUrl}
                  className="flex-1"
                >
                  {isRendering ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Rendering...
                    </>
                  ) : (
                    <>
                      <Disc3 className="h-4 w-4 mr-2" />
                      Render Mix
                    </>
                  )}
                </Button>
                <Button onClick={handleDownload} disabled={!renderedBlob || isRendering} className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download WAV
                </Button>
              </div>

              {renderError && <p className="text-sm text-destructive">{renderError}</p>}
            </div>

            {createProjectMutation.isError && (
              <p className="text-sm text-destructive">Failed to create project. Please try again.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
